import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  FolderOpen, Server, Wifi, WifiOff, AlertTriangle, Info, 
  Shield, Lock, Database, HardDrive, ArrowLeft, Settings,
  FileText, Upload, Plus, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';
const enconPurple = '#9333ea';

const Projects = ({ user }) => {
  const navigate = useNavigate();
  const [networkStatus, setNetworkStatus] = useState('checking'); // 'checking', 'connected', 'disconnected'
  const [localServerUrl, setLocalServerUrl] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Check network/local server connectivity
  useEffect(() => {
    checkNetworkConnection();
    
    // Re-check every 30 seconds
    const interval = setInterval(checkNetworkConnection, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkNetworkConnection = async () => {
    setNetworkStatus('checking');
    
    try {
      // First, get the configured local server URL from settings
      const settingsResponse = await api.get(`${API}/settings`);
      const configuredUrl = settingsResponse.data?.local_server_url;
      setLocalServerUrl(configuredUrl);
      
      if (!configuredUrl) {
        // No local server configured
        setNetworkStatus('not_configured');
        return;
      }
      
      // Try to ping the local server
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(`${configuredUrl}/health`, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors'
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          setNetworkStatus('connected');
        } else {
          setNetworkStatus('disconnected');
        }
      } catch (e) {
        setNetworkStatus('disconnected');
      }
    } catch (error) {
      console.error('Error checking network:', error);
      setNetworkStatus('not_configured');
    }
  };

  // Example project data for demonstration
  const exampleProject = {
    id: 'example-001',
    name: 'Example Project - Local Server Demo',
    customer: 'Demo Customer Ltd',
    status: 'In Progress',
    created: '2026-01-29',
    description: 'This is an example project demonstrating how data would appear when connected to a local server.',
    items: [
      { code: 'PROD-001', description: 'Example Product 1', quantity: 10 },
      { code: 'PROD-002', description: 'Example Product 2', quantity: 25 },
      { code: 'PROD-003', description: 'Example Product 3', quantity: 5 },
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#ededed' }}>
      <AppHeader
        user={user}
        subHeaderLeft={
          <button
            onClick={() => navigate('/sales-dashboard')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors text-gray-700"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to Sales Dashboard</span>
          </button>
        }
      />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Network Status Banner */}
        <div className={`rounded-xl p-4 mb-6 border-2 ${
          networkStatus === 'connected' ? 'bg-green-50 border-green-400' :
          networkStatus === 'disconnected' ? 'bg-red-50 border-red-400' :
          networkStatus === 'not_configured' ? 'bg-yellow-50 border-yellow-400' :
          'bg-gray-50 border-gray-400'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {networkStatus === 'checking' && (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-500 border-t-transparent" />
                  <span className="font-medium text-gray-700">Checking network connection...</span>
                </>
              )}
              {networkStatus === 'connected' && (
                <>
                  <Wifi className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-700">Connected to Local Server</span>
                  <span className="text-sm text-green-600">({localServerUrl})</span>
                </>
              )}
              {networkStatus === 'disconnected' && (
                <>
                  <WifiOff className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-700">Not Connected to Local Network</span>
                  <span className="text-sm text-red-600">Connect to office network or VPN to access</span>
                </>
              )}
              {networkStatus === 'not_configured' && (
                <>
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-700">Local Server Not Configured</span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInfoModal(true)}
                className="gap-2"
              >
                <Info className="w-4 h-4" />
                Learn More
              </Button>
              {user?.role === 'super_admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConfigModal(true)}
                  className="gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Configure
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Local Storage Info Card */}
            <Card className="border-2" style={{ borderColor: enconPurple }}>
              <CardHeader className="bg-purple-50">
                <CardTitle className="text-purple-700 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Protected Data Zone
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Network Protected</p>
                    <p className="text-xs text-gray-500">Only accessible on local network or VPN</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <HardDrive className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Local Storage</p>
                    <p className="text-xs text-gray-500">All data stored on your local server</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Database className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Isolated Database</p>
                    <p className="text-xs text-gray-500">Separate from cloud infrastructure</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Architecture Diagram */}
            <Card className="border-2 border-gray-300">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  System Architecture
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs">
                <div className="bg-gray-50 rounded-lg p-3 font-mono space-y-2">
                  <div className="text-center border-b pb-2 mb-2">
                    <span className="text-orange-600">☁️ Cloud App</span>
                  </div>
                  <div className="text-center text-gray-400">↓ Network Check ↓</div>
                  <div className="text-center border-t pt-2 mt-2">
                    <span className="text-purple-600">🏢 Local Server</span>
                    <div className="text-gray-500 mt-1">\\birminghamfs1</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Projects Area */}
          <div className="lg:col-span-2">
            {/* Example Project Card */}
            <Card className="border-2 border-dashed border-orange-300 bg-orange-50/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-orange-700 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5" />
                    Example Project (Demo)
                  </CardTitle>
                  <span className="px-3 py-1 bg-orange-200 text-orange-800 rounded-full text-xs font-medium">
                    EXAMPLE DATA
                  </span>
                </div>
                <CardDescription>
                  This demonstrates how projects will appear when connected to your local server
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Project Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Project Name:</span>
                    <p className="font-medium">{exampleProject.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <p className="font-medium">{exampleProject.customer}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <p className="font-medium text-green-600">{exampleProject.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <p className="font-medium">{exampleProject.created}</p>
                  </div>
                </div>

                {/* Example Items */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 font-medium text-sm">Project Items</div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exampleProject.items.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">{item.code}</td>
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2 text-right">{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">This is demonstration data only</p>
                      <p className="text-blue-600">
                        Once a local server is configured and you're connected to the office network, 
                        this page will display real project data stored securely on your local infrastructure.
                        All uploads, edits, and data will be saved directly to your local server.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons (Disabled) */}
            <div className="flex gap-3 mt-4">
              <Button
                disabled
                className="gap-2 opacity-50"
                style={{ backgroundColor: enconOrange }}
              >
                <Plus className="w-4 h-4" />
                Create New Project
              </Button>
              <Button
                disabled
                variant="outline"
                className="gap-2 opacity-50"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </Button>
              <span className="text-sm text-gray-500 self-center ml-2">
                (Requires local server connection)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconPurple }}>
              <Shield className="w-6 h-6" />
              Local Server Architecture
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-bold text-purple-800 mb-2">Why Local Storage?</h3>
              <p className="text-sm text-purple-700">
                Sensitive project and customer data is stored on your local server infrastructure 
                rather than in the cloud. This provides an additional layer of security and ensures 
                data never leaves your network.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-bold">How It Works:</h3>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Network Detection</p>
                  <p className="text-sm text-gray-600">The system checks if you're connected to the office network or VPN</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Server Verification</p>
                  <p className="text-sm text-gray-600">Confirms the local server is reachable and responding</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Secure Data Access</p>
                  <p className="text-sm text-gray-600">All project data is fetched from and saved to your local server</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Requirements
              </h3>
              <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                <li>Local server running on your network</li>
                <li>Connection via office LAN or VPN</li>
                <li>Server URL configured in settings</li>
              </ul>
            </div>

            <div className="text-sm text-gray-500">
              <p>For setup instructions, see the <strong>LOCAL_SERVER_GUIDE.md</strong> document in the code editor.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Configuration Modal (Super Admin Only) */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconOrange }}>
              <Settings className="w-6 h-6" />
              Local Server Configuration
            </DialogTitle>
            <DialogDescription>
              Configure the local server URL for protected data storage
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-3">
                The local server URL should point to a server running on your local network.
                Examples:
              </p>
              <ul className="text-sm font-mono space-y-1">
                <li className="text-gray-700">http://192.168.1.100:8001</li>
                <li className="text-gray-700">http://birminghamfs1.local:8001</li>
                <li className="text-gray-700">http://intranet.company.local:8001</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> A local server application needs to be running on your network 
                to receive and store data. See the setup guide for instructions.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() => {
                setShowConfigModal(false);
                navigate('/settings');
              }}
              className="w-full"
            >
              Go to Settings to Configure
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
